<template>
  <li class="team-member-card flex flex-col bg-white shadow-1dp rounded p-15 tablet:p-20">
    <div class="flex items-start">
      <img
        v-if="teamMember.photoURL && !photoFailed"
        :src="teamMember.photoURL"
        alt=""
        class="flex-shrink-0 h-44 w-44 rounded-full object-cover bg-gray-200"
        @error="photoFailed = true"
      >
      <AvatarChip
        v-else
        :size="44"
        class="flex-shrink-0 text-xl bg-gray-200"
      >
        {{ initial }}
      </AvatarChip>
      <div class="flex-1 min-w-0 ml-12">
        <p
          :title="teamMember.name"
          class="font-bold text-lg text-primary leading-tight truncate"
        >
          {{ teamMember.name }}
        </p>
        <p class="text-sm text-gray-600 leading-snug mt-2">
          {{ teamMemberRoles[teamMember.role] || teamMember.role }}
          <span
            v-if="teamMember.maxGuests && teamMember.role !== 'guestsCheckIn'"
            class="whitespace-no-wrap"
          >
            · max. {{ Number(teamMember.maxGuests).toLocaleString() }} guests
          </span>
        </p>
        <p
          :title="statusTooltip"
          class="status-chip inline-flex items-center font-medium text-xs leading-none py-5 px-8 mt-8 rounded"
          :class="isAccepted ? 'is-accepted' : 'is-pending'"
        >
          <FontAwesomeIcon
            :icon="isAccepted ? ['fas', 'check-circle'] : ['far', 'clock']"
            class="mr-5"
          />
          {{ isAccepted ? 'Invitation accepted' : 'Invitation pending' }}
        </p>
      </div>
      <ElDropdown
        trigger="click"
        title="Edit team member"
        class="flex-shrink-0 -mt-6 -mr-8"
        @click.native.stop
      >
        <ElButton
          title="Additional options"
          circle
          class="text-primary bg-transparent border-none"
        >
          <FontAwesomeIcon
            :icon="['far', 'ellipsis-v']"
            class="icon-square-size text-xl"
          />
        </ElButton>
        <ElDropdownMenu slot="dropdown">
          <ElDropdownItem @click.native="$emit('update', teamMember)">
            <FontAwesomeIcon
              fixed-width
              :icon="['fas', 'pen']"
              class="text-lg ml-5 mr-15"
            />
            Edit
          </ElDropdownItem>
          <ElDropdownItem
            class="text-danger"
            @click.native="$emit('remove', teamMember)"
          >
            <FontAwesomeIcon
              fixed-width
              :icon="['fas', 'trash']"
              class="text-lg ml-5 mr-15"
            />
            Remove
          </ElDropdownItem>
        </ElDropdownMenu>
      </ElDropdown>
    </div>

    <div class="text-sm text-primary border-t border-gray-200 pt-12 mt-15">
      <p
        class="flex items-center"
        :title="`Login email: ${teamMember.email}`"
      >
        <FontAwesomeIcon
          :icon="['fas', 'envelope']"
          fixed-width
          class="flex-shrink-0 text-gray-500 mr-10"
        />
        <span
          data-private
          class="truncate"
        >{{ teamMember.email }}</span>
      </p>
      <p class="flex items-center mt-8">
        <FontAwesomeIcon
          :icon="['fas', 'key']"
          fixed-width
          class="flex-shrink-0 text-gray-500 mr-10"
        />
        <span
          v-if="!teamMember.password"
          class="text-primary-light italic"
        >
          Password set by the member
        </span>
        <template v-else>
          <span
            data-private
            class="password-value truncate"
          >{{ passwordIsVisible ? teamMember.password : '••••••••' }}</span>
          <button
            type="button"
            class="flex-shrink-0 text-primary-light hover:text-primary focus:outline-none ml-10"
            :title="passwordIsVisible ? 'Hide password' : 'Show password'"
            @click="passwordIsVisible = !passwordIsVisible"
          >
            <FontAwesomeIcon
              fixed-width
              :icon="passwordIsVisible ? ['fas', 'eye-slash'] : ['fas', 'eye']"
            />
          </button>
        </template>
      </p>
    </div>
  </li>
</template>

<script>
import AvatarChipNew from '@/components/AvatarChipNew';

const teamMemberRoles = {
  eventOwner: 'Event owner',
  guestsCheckIn: 'Guest check-in/out only',
  guestsFullAccess: 'Manager',
  eventPromoter: 'Promoter',
};

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export default {
  props: {
    teamMember: {
      type: Object,
    },
  },
  components: {
    AvatarChip: AvatarChipNew,
  },
  data: () => ({
    photoFailed: false,
    passwordIsVisible: false,
    teamMemberRoles,
  }),
  watch: {
    'teamMember.photoURL': function onPhotoChange() {
      this.photoFailed = false;
    },
  },
  computed: {
    initial() {
      return (this.teamMember.name || '?').trimStart().charAt(0);
    },
    // status 1 is written by the backend (refreshTeamMembersStatus) once the member signed in after the invitation
    isAccepted() {
      return this.teamMember.status === 1;
    },
    statusTooltip() {
      const lastSignIn = toDate(this.teamMember.lastSignInAt);
      const invitedAt = toDate(this.teamMember.invitedAt);
      if (this.isAccepted) {
        return lastSignIn ? `Last signed in on ${lastSignIn.toLocaleString()}` : 'The member has signed in';
      }
      return invitedAt
        ? `Invited on ${invitedAt.toLocaleString()}, has not signed in since`
        : 'The member has not signed in yet';
    },
  },
};
</script>

<style scoped>
.status-chip.is-accepted {
  @apply text-status-checked-in bg-status-checked-in-15;
}

.status-chip.is-pending {
  @apply text-gray-700 bg-gray-200;
}

.password-value {
  font-family: Menlo, Consolas, 'Courier New', monospace;
}
</style>
